import { useEffect, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Card, CardContent } from '@pantry/ui';
import { getTourById, hasCompletedOnOtherPlatform } from '@pantry/api-client';
import { translateTourField } from '@pantry/i18n';
import {
  XMarkIcon,
  RocketLaunchIcon,
  ClockIcon,
  XCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useTourContext } from './useTourContext';

/**
 * TourPrompt displays a dialog prompting the user to start a tour.
 * Shows three options: Start Tour, Remind Me Later, Skip Forever.
 */
export function TourPrompt() {
  const { t, i18n } = useLingui();
  const { tourToPrompt, tours, startTour, remindLater, skipTour, dismissPrompt } = useTourContext();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tourToPrompt) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissPrompt();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [tourToPrompt, dismissPrompt]);

  if (!tourToPrompt) return null;

  const definition = getTourById(tourToPrompt);
  const tourProgress = tours?.find((tour) => tour.tourId === tourToPrompt);

  if (!definition) return null;

  const completedOnMobile = tourProgress ? hasCompletedOnOtherPlatform(tourProgress, 'web') : false;
  const hasVersionUpdate = tourProgress?.web?.hasVersionUpdate === true;

  const handleStart = async () => {
    await startTour(tourToPrompt);
  };

  const handleRemindLater = async () => {
    await remindLater();
  };

  const handleSkip = async () => {
    await skipTour('not_interested');
  };

  const stepsCount = definition.steps.web?.length ?? 0;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={translateTourField(i18n, tourToPrompt, 'name', definition.name)}
      onClick={(e) => e.target === e.currentTarget && dismissPrompt()}
    >
      <Card className="w-full max-w-md animate-in slide-in-from-bottom-4 duration-300 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <RocketLaunchIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground">
                {translateTourField(i18n, tourToPrompt, 'name', definition.name)}
              </h2>
              <p className="text-sm text-muted-foreground">
                {stepsCount > 0 && <Trans>{stepsCount} quick steps</Trans>}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-full hover:bg-muted"
            onClick={dismissPrompt}
            aria-label={t`Close`}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        <CardContent className="pt-0 space-y-6">
          {/* Description */}
          <p className="text-muted-foreground leading-relaxed">
            {translateTourField(i18n, tourToPrompt, 'description', definition.description)}
          </p>

          {/* Completed on mobile indicator */}
          {completedOnMobile && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-fresh/10 border border-fresh/20">
              <CheckCircleIcon className="h-5 w-5 text-fresh flex-shrink-0" />
              <p className="text-sm text-fresh">
                <Trans>You completed this tour on mobile!</Trans>
              </p>
            </div>
          )}

          {/* Updated tour indicator */}
          {hasVersionUpdate && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="text-sm text-primary">
                <Trans>This tour has been updated with new steps.</Trans>
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            {/* Primary action - Start Tour */}
            <Button
              onClick={handleStart}
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            >
              <RocketLaunchIcon className="h-5 w-5 mr-2" />
              <Trans>Start Tour</Trans>
            </Button>

            {/* Secondary actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleRemindLater}
                className="flex-1 h-11 rounded-xl"
              >
                <ClockIcon className="h-4 w-4 mr-2" />
                <Trans>Remind Later</Trans>
              </Button>

              <Button
                variant="ghost"
                onClick={handleSkip}
                className="flex-1 h-11 rounded-xl text-muted-foreground hover:text-foreground"
              >
                <XCircleIcon className="h-4 w-4 mr-2" />
                <Trans>Skip</Trans>
              </Button>
            </div>
          </div>

          {/* Skip reason hint */}
          <p className="text-xs text-center text-muted-foreground">
            <Trans>You can always restart the tour from Settings</Trans>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
